/**
 * Admin CRUD for per-tour Maya content (sections + custom system prompt).
 * Gated to admin/owner. Feeds `/api/destination-chat` and future Maya
 * per-tour experiences via the `tour_maya_content` table.
 */
import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Trash2, Sparkles, Search } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import {
  listTourMayaContent,
  upsertTourMayaContent,
  deleteTourMayaContent,
  type TourMayaContentRow,
} from "@/lib/tour-maya-content.functions";

export const Route = createFileRoute("/_authenticated/admin/maya-tour-content")({
  head: () => ({
    meta: [
      { title: "Maya · Contenido por tour" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

const LANGS = ["es", "en", "fr", "pt", "it", "de"] as const;
type Lang = (typeof LANGS)[number];

interface FormState {
  id: string | null;
  tourSlug: string;
  lang: Lang;
  title: string;
  summary: string;
  sectionsJson: string;
  mayaSystemPrompt: string;
  status: "draft" | "published";
}

const EMPTY: FormState = {
  id: null,
  tourSlug: "",
  lang: "es",
  title: "",
  summary: "",
  sectionsJson: "{\n  \"itinerary\": \"\",\n  \"includes\": [],\n  \"excludes\": [],\n  \"schedule\": \"\",\n  \"tips\": [],\n  \"history\": \"\"\n}",
  mayaSystemPrompt: "",
  status: "draft",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function Page() {
  const qc = useQueryClient();
  const listFn = useServerFn(listTourMayaContent);
  const upsertFn = useServerFn(upsertTourMayaContent);
  const deleteFn = useServerFn(deleteTourMayaContent);

  const [search, setSearch] = useState("");
  const [langFilter, setLangFilter] = useState<Lang | "">("");
  const [form, setForm] = useState<FormState>(EMPTY);
  const [notice, setNotice] = useState<{
    kind: "ok" | "err";
    msg: string;
  } | null>(null);

  const list = useQuery({
    queryKey: ["tour-maya-content", search, langFilter],
    queryFn: () =>
      listFn({
        data: {
          search: search.trim() || undefined,
          lang: langFilter || undefined,
        },
      }),
    retry: false,
  });

  const forbidden = useMemo(
    () =>
      list.isError &&
      /forbidden/i.test((list.error as Error | null)?.message ?? ""),
    [list.isError, list.error],
  );

  const saveMut = useMutation({
    mutationFn: async () => {
      let sections: unknown = {};
      const raw = form.sectionsJson.trim();
      if (raw) {
        try {
          sections = JSON.parse(raw);
        } catch {
          throw new Error("El campo 'Sections (JSON)' no es JSON válido");
        }
      }
      return upsertFn({
        data: {
          tourSlug: form.tourSlug.trim(),
          lang: form.lang,
          title: form.title.trim() || null,
          summary: form.summary.trim() || null,
          sections: sections as never,
          mayaSystemPrompt: form.mayaSystemPrompt.trim() || null,
          status: form.status,
        },
      });
    },
    onSuccess: (row) => {
      setNotice({ kind: "ok", msg: `Guardado (${row.tour_slug} · ${row.lang})` });
      qc.invalidateQueries({ queryKey: ["tour-maya-content"] });
      loadRow(row);
    },
    onError: (e: Error) => setNotice({ kind: "err", msg: e.message }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      setNotice({ kind: "ok", msg: "Eliminado" });
      qc.invalidateQueries({ queryKey: ["tour-maya-content"] });
      setForm(EMPTY);
    },
    onError: (e: Error) => setNotice({ kind: "err", msg: e.message }),
  });

  function loadRow(row: TourMayaContentRow) {
    setForm({
      id: row.id,
      tourSlug: row.tour_slug,
      lang: (row.lang as Lang) ?? "es",
      title: row.title ?? "",
      summary: row.summary ?? "",
      sectionsJson: JSON.stringify(row.sections ?? {}, null, 2),
      mayaSystemPrompt: row.maya_system_prompt ?? "",
      status: (row.status as "draft" | "published") ?? "draft",
    });
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center gap-3">
        <Sparkles className="h-5 w-5 text-primary" />
        <h1 className="font-display text-2xl font-semibold">
          Maya · Contenido por tour
        </h1>
      </div>
      <AdminNav />

      {forbidden && (
        <p className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Sin permisos. Se requiere rol admin u owner.
        </p>
      )}

      {notice && (
        <p
          className={`mt-4 rounded-md border p-3 text-sm ${
            notice.kind === "ok"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
              : "border-destructive/40 bg-destructive/10 text-destructive"
          }`}
        >
          {notice.msg}
        </p>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        {/* List */}
        <section className="rounded-2xl border border-border bg-card p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por slug…"
                className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-2 text-sm"
              />
            </div>
            <select
              value={langFilter}
              onChange={(e) => setLangFilter((e.target.value as Lang) || "")}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="">Todos</option>
              {LANGS.map((l) => (
                <option key={l} value={l}>
                  {l.toUpperCase()}
                </option>
              ))}
            </select>
            <button
              onClick={() => setForm(EMPTY)}
              className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Nuevo
            </button>
          </div>

          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            {list.isLoading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Loader2 className="mx-auto h-4 w-4 animate-spin" />
              </div>
            ) : list.data && list.data.length > 0 ? (
              <ul className="divide-y divide-border">
                {list.data.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => loadRow(row)}
                      className={`flex w-full items-start justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent ${
                        form.id === row.id ? "bg-primary/5" : ""
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="truncate font-medium">
                          {row.tour_slug}{" "}
                          <span className="ml-1 text-xs uppercase text-muted-foreground">
                            · {row.lang}
                          </span>
                        </div>
                        <div className="mt-0.5 truncate text-xs text-muted-foreground">
                          {row.title ?? "(sin título)"} · actualizado{" "}
                          {fmtDate(row.updated_at)}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                          row.status === "published"
                            ? "bg-emerald-500/15 text-emerald-700"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {row.status}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-6 text-center text-sm text-muted-foreground">
                Sin registros.
              </p>
            )}
          </div>
        </section>

        {/* Editor */}
        <section className="rounded-2xl border border-border bg-card p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block font-medium">Slug del tour</span>
              <input
                value={form.tourSlug}
                onChange={(e) =>
                  setForm((f) => ({ ...f, tourSlug: e.target.value }))
                }
                placeholder="chichen-itza-clasico"
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Idioma</span>
              <select
                value={form.lang}
                onChange={(e) =>
                  setForm((f) => ({ ...f, lang: e.target.value as Lang }))
                }
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                {LANGS.map((l) => (
                  <option key={l} value={l}>
                    {l.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="mb-1 block font-medium">Título</span>
              <input
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="mb-1 block font-medium">Resumen</span>
              <textarea
                value={form.summary}
                onChange={(e) =>
                  setForm((f) => ({ ...f, summary: e.target.value }))
                }
                rows={2}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="mb-1 block font-medium">
                Sections (JSON) — itinerario, incluye, no incluye, horarios,
                consejos, historia…
              </span>
              <textarea
                value={form.sectionsJson}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sectionsJson: e.target.value }))
                }
                rows={12}
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 font-mono text-xs"
              />
            </label>
            <label className="text-sm sm:col-span-2">
              <span className="mb-1 block font-medium">
                Maya System Prompt (opcional — sobrescribe el prompt genérico)
              </span>
              <textarea
                value={form.mayaSystemPrompt}
                onChange={(e) =>
                  setForm((f) => ({ ...f, mayaSystemPrompt: e.target.value }))
                }
                rows={6}
                placeholder="Eres una guía cultural experta en Chichén Itzá Clásico…"
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Estado</span>
              <select
                value={form.status}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    status: e.target.value as "draft" | "published",
                  }))
                }
                className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              >
                <option value="draft">Borrador</option>
                <option value="published">Publicado</option>
              </select>
            </label>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              disabled={saveMut.isPending || !form.tourSlug.trim()}
              onClick={() => saveMut.mutate()}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saveMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Guardar
            </button>
            {form.id && (
              <button
                disabled={deleteMut.isPending}
                onClick={() => {
                  if (confirm("¿Eliminar este contenido?")) {
                    deleteMut.mutate(form.id as string);
                  }
                }}
                className="inline-flex items-center gap-2 rounded-md border border-destructive/40 px-4 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-50"
              >
                {deleteMut.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Eliminar
              </button>
            )}
            <button
              onClick={() => setForm(EMPTY)}
              className="rounded-md border border-input px-3 py-2 text-sm font-medium hover:bg-accent"
            >
              Limpiar
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
